/**
 * The 25 question categories (SYR-111 phase 3).
 *
 * The elements above are derived. These are where the statements land, and
 * where a fixable problem is still legible: once a category is blended upward
 * it stops reading as something a brand can go and change.
 */

import React from 'react';
import { Card, Space, Table, Typography } from 'antd';
import { THEME } from '../../config/theme';
import { getDisplayName } from '../../../../utils/elementDisplayNames';

const { Text, Paragraph } = Typography;

interface CategoryRow {
  category: string;
  meanScore: number | null;
  statements?: unknown[];
}

interface Props {
  consensus: { result?: { questionCategoryScores?: CategoryRow[] } } | null;
}

export const CategoryScores: React.FunctionComponent<Props> = ({ consensus }) => {
  const rows = (consensus?.result?.questionCategoryScores ?? []).filter(
    (row) => row && row.category,
  );
  if (rows.length === 0) return null;

  const sorted = [...rows].sort(
    (a, b) => (b.meanScore ?? -101) - (a.meanScore ?? -101),
  );

  return (
    <Card title="What was actually measured">
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <Table
          size="small"
          pagination={false}
          rowKey="category"
          dataSource={sorted}
          columns={[
            {
              title: 'Category',
              dataIndex: 'category',
              render: (name: string) => getDisplayName(name),
            },
            {
              title: 'Score',
              dataIndex: 'meanScore',
              width: 110,
              render: (value: number | null) => (
                <Text strong={value !== null && Math.abs(value) > 50}>
                  {value === null ? '—' : Math.round(value)}
                </Text>
              ),
            },
            {
              title: 'Statements',
              dataIndex: 'statements',
              width: 120,
              render: (statements: unknown[] | undefined) => (
                <Text style={{ color: THEME.textSecondary }}>
                  {statements?.length ?? 0}
                </Text>
              ),
            },
          ]}
        />
        <Paragraph style={{ color: THEME.textSecondary, marginBottom: 0, fontSize: 13, maxWidth: '72ch' }}>
          Read these with the scale in mind. Price Deals runs on a −100…0 scale and is
          structurally low for every brand, exactly as Touchpoints is structurally high; neither
          is ever the headline.
        </Paragraph>
      </Space>
    </Card>
  );
};
